import { Link, Navigate, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { AuthRequired } from "@/components/auth/AuthRequired";
import { SubPage } from "@/components/profile/SettingsKit";
import { useUrlState } from "@/lib/useUrlState";
import { fetchCryptoOptions, fetchWalletCapabilities, keys } from "@/lib/queries";
import { Badge, Button, Card, EmptyState, Skeleton } from "@/components/ui";
import { CurrencyMark, type CurrencyCode } from "@/currency";
import { useCurrencySettings } from "@/currency/CurrencySettingsProvider";
import { MODE_GAME, resolveWalletCurrency, useTradingMode } from "@/mode";
import { ArrowDownToLine, ArrowUpFromLine, Wallet, type LucideIcon } from "@/icons";
import { cn } from "@/lib/cn";
import { CryptoDepositPanel } from "@/components/wallet/crypto/CryptoDepositPanel";
import { CryptoWithdrawPanel } from "@/components/wallet/crypto/CryptoWithdrawPanel";
import { AccountFundingDisabled } from "@/components/wallet/AccountFunding";
import type { RailBlock, RailDirection } from "@/components/wallet/crypto/currencyScope";
import { useSessionAccount } from "@/lib/useSessionAccount";
import { REASON_ACCOUNT_MODE, reasonForError } from "@/lib/capabilityReason";
import type { WalletCapabilityEntry } from "@/lib/types";
import { buildHead } from "@/lib/seo";

// /wallet/crypto — the custodial crypto rail (SM-212): Deposit / Withdraw
// over /api/wallet/crypto/*. CUSTODIAL ONLY in v1 — no wallet-connect of any
// kind; the user sends to a per-user deposit address and withdrawals are
// signed server-side.
//
// There is deliberately NO history tab: completed movements are ledger rows
// (crypto rides the same deposit/withdrawal lifecycle as every rail), so they
// already appear in Portfolio → History. Only the in-flight slice is shown
// here, inside each panel (CryptoInFlight).
//
// ── SM-508: one page, scoped to a currency ───────────────────────────
//
// This page used to refuse GAME outright — it "moved Gold, a Cash-mode
// concern". That made Game's Deposit and Withdraw a different journey
// through a different screen, which is the duplication the ticket
// closes. What actually differs between the two modes is not the screen
// but the CURRENCY, so the scope became a parameter and the refusal
// became a narrowing:
//
//	CASH  → GOLD, and `?currency=` need not be spelled: Cash has
//	        exactly one currency, so naming it would imply a choice.
//	GAME  → RED, the Game token, spelled explicitly in the link so the
//	        deep link says what it opens.
//
// The mode is still the authority over the scope — `resolveWalletCurrency`
// only ever NARROWS to what the mode already shows — so no hand-typed
// `?currency=GOLD` can put a Cash rail in front of a Game session. When
// one is typed, the URL is rewritten to the resolved answer rather than
// left disagreeing with the page.
//
// What may actually be done is still never decided here. Three layers
// answer, deny-wins from the outside in: the ledger's effective
// capability for the currency (authoritative, SM-419), the rail's own
// kill switch, and the SM-504 (MODE → CURRENCY → ASSET + CURRENCY)
// route resolution. A shut direction is rendered with its level and
// reason, never dropped.
//
// SM-764: a session whose broker account is funded by its broker
// (bootstrap balance_mode other than SYSTEM_BROKER) has no native rail
// at all. The page then renders the account-funding state and fetches
// nothing — no catalog, no address — and the gateway / crypto-wallet-
// service refuse every rail call for it (NATIVE_FUNDING_DISABLED) should
// one be made by hand. A deep link lands here, not on a form.
export const Route = createFileRoute("/_app/wallet/crypto")({
  head: () =>
    buildHead({
      path: "/wallet/crypto",
      title: "Crypto wallet",
      description: "Deposit and withdraw crypto.",
      noindex: true,
    }),
  validateSearch: (s: Record<string, unknown>): { tab?: string; currency?: string } => ({
    tab: typeof s.tab === "string" ? s.tab : undefined,
    // Kept RAW here on purpose. Resolution needs the active mode, which
    // a search validator cannot see, so the component narrows it — and
    // holding the raw value is what lets it notice a rewrite is due.
    currency: typeof s.currency === "string" ? s.currency : undefined,
  }),
  component: () => (
    <AuthRequired>
      <CryptoWalletPage />
    </AuthRequired>
  ),
});

function CryptoWalletPage() {
  const { t } = useTranslation("wallet");
  const navigate = useNavigate();
  const { mode, resolving } = useTradingMode();
  const { currency: requestedCurrency } = Route.useSearch();
  const [tab, setTab] = useUrlState("tab", "deposit");
  const { nativeFundingAllowed, known } = useSessionAccount();
  // The scope, resolved before the queries so the catalog request can
  // name it. `resolving` is honoured below — while the stored mode is
  // still in flight this is only the CASH default, and the page renders
  // a skeleton rather than that answer.
  const scopedCurrency = resolveWalletCurrency(mode, requestedCurrency);
  const optionsQ = useQuery({
    // Scoped to the currency this page is moving (SM-514 AC 3). The
    // catalog then answers about that currency throughout — including
    // the legacy single-rate fields, which otherwise describe whichever
    // route happens to be first and quoted Gold's rate on a Red screen.
    queryKey: keys.walletCryptoOptions(scopedCurrency),
    queryFn: () => fetchCryptoOptions(scopedCurrency),
    staleTime: 60_000,
    enabled: nativeFundingAllowed,
  });
  // The ledger's own answer per currency and direction — the layer that
  // actually enforces. Folded in above the rail so a currency the
  // ledger has shut can never render a form, whatever the rail catalog
  // says.
  const capsQ = useQuery({
    queryKey: keys.walletCapabilities(),
    queryFn: fetchWalletCapabilities,
    staleTime: 60_000,
    enabled: nativeFundingAllowed,
  });
  const options = optionsQ.data;

  // Hide the Withdraw mode when the RAIL has withdrawals switched off; a
  // URL-forced ?tab=withdraw still lands on the panel's friendly empty state.
  // A currency-level block does NOT hide the tab — the user needs to be
  // able to reach the reason (AC8), and only a rail that is off entirely
  // has nothing to explain.
  const withdrawEnabled = !options || options.withdraw_enabled;

  // Hidden by bootstrap, or refused by the server for a session the
  // bootstrap called native (a stale tab, a tampered client): either way
  // the account is funded by its broker, and that — not "couldn't load,
  // try again" — is what the page says.
  const refused = [optionsQ.error, capsQ.error].some((e) => reasonForError(e) === REASON_ACCOUNT_MODE);
  if ((known && !nativeFundingAllowed) || refused) {
    return (
      <SubPage title={t("crypto.pageTitle")} onBack={() => navigate({ to: "/portfolio" })} bodyClassName="max-w-4xl">
        <AccountFundingDisabled className="my-6" />
      </SubPage>
    );
  }

  // Cold load: the stored mode is still in flight, so `mode` is only the
  // CASH default. Scoping on it would hand a GAME user the Gold rail on
  // every refresh and every pasted link — the SM-487 trap, one page over.
  if (resolving || !known) {
    return (
      <SubPage title={t("crypto.pageTitle")}>
        <Skeleton className="h-40 w-full" />
      </SubPage>
    );
  }

  const currency = scopedCurrency;
  // The URL asked for something this mode does not show. Rewrite it, so
  // the address bar, the page and a re-share all agree; `replace` keeps
  // the rejected address out of the back stack.
  if (requestedCurrency !== undefined && requestedCurrency !== currency) {
    return <Navigate to="/wallet/crypto" search={{ tab, currency }} replace />;
  }

  const direction: RailDirection = tab === "withdraw" ? "withdraw" : "deposit";
  const ledgerBlock = ledgerBlockFor(capsQ.data?.capabilities, currency, direction);

  return (
    <SubPage
      title={t("crypto.pageTitle")}
      subtitle={t("crypto.pageSubtitle")}
      onBack={() => navigate({ to: "/portfolio" })}
      bodyClassName="max-w-4xl"
    >
      <CurrencyScope currency={currency} game={mode === MODE_GAME} />
      <ModeSwitch value={tab} onChange={setTab} withdrawEnabled={withdrawEnabled} />
      <div className="space-y-6 pt-5 pb-4">
        {optionsQ.isLoading || capsQ.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-11 rounded-xl" />
            <Skeleton className="h-14 rounded-xl" />
            <Skeleton className="h-40 rounded-xl" />
          </div>
        ) : optionsQ.isError || !options ? (
          <EmptyState
            icon={<Wallet />}
            title={t("crypto.loadErrorTitle")}
            description={t("crypto.loadErrorDescription")}
            action={
              <Button variant="outline" onClick={() => optionsQ.refetch()}>
                {t("crypto.retry")}
              </Button>
            }
          />
        ) : direction === "withdraw" ? (
          <CryptoWithdrawPanel options={options} currency={currency} blockedBy={ledgerBlock} />
        ) : (
          <CryptoDepositPanel options={options} currency={currency} blockedBy={ledgerBlock} />
        )}
      </div>
      <p className="pb-8 text-center text-xs text-fg-muted">
        <Trans
          t={t}
          i18nKey="crypto.historyFootnote"
          components={{
            link: (
              <Link
                to="/portfolio"
                search={{ tab: "history" }}
                className="font-medium text-primary hover:underline"
              />
            ),
          }}
        />
      </p>
    </SubPage>
  );
}

/**
 * The ledger's verdict for one currency and direction, or null when it
 * has no objection.
 *
 * A currency the payload does not mention is "no opinion", not a
 * refusal: an older ledger that lists only GOLD must not be read as
 * having shut everything else, or a rollout would blank the Cash rail.
 * The rail's own resolution still runs underneath either way, and the
 * server enforces regardless of what any of this renders.
 */
function ledgerBlockFor(
  capabilities: WalletCapabilityEntry[] | undefined,
  currency: CurrencyCode,
  direction: RailDirection,
): RailBlock | null {
  const entry = capabilities?.find((c) => c.currency === currency);
  if (!entry) return null;
  const verdict = direction === "deposit" ? entry.deposit : entry.withdraw;
  if (verdict.allowed) return null;
  return { level: "LEDGER", code: verdict.reason };
}

/**
 * Which money this page is moving — the one thing a shared Cash/Game
 * screen must never leave the reader to infer (AC11).
 *
 * Mark, name and mode together, because each alone can be misread: the
 * marks differ largely by hue, which is no difference at all to a
 * colour-blind reader, and a currency name without the mode does not
 * tell a user whether they are about to touch their real balance.
 */
function CurrencyScope({ currency, game }: { currency: CurrencyCode; game: boolean }) {
  const { t } = useTranslation("wallet");
  const settings = useCurrencySettings();
  const name = settings[currency]?.displayName?.trim() || t(`currency.${currency}`);
  // Precomputed: the badge's copy is one of two whole sentences, and a
  // ternary inside the attribute is how the two drift apart.
  const modeLabel = game ? t("scope.gameMode") : t("scope.cashMode");

  return (
    <Card
      data-testid="wallet-currency-scope"
      data-currency={currency}
      className="mb-3 flex items-center justify-between gap-3 bg-surface-2 p-3.5"
    >
      <span className="flex min-w-0 items-center gap-2">
        <CurrencyMark aria-hidden currency={currency} settings={settings} size={22} />
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-fg">
            {t("scope.movingCurrency", { currency: name })}
          </span>
          <span className="block truncate text-xs text-fg-muted">{t("scope.movingHint")}</span>
        </span>
      </span>
      <Badge tone={game ? "primary" : "neutral"}>{modeLabel}</Badge>
    </Card>
  );
}

// The Deposit ↔ Withdraw switcher — two roomy cards instead of a hairline tab
// strip, so each mode reads as a plain-language action on every screen size.
function ModeSwitch({
  value,
  onChange,
  withdrawEnabled,
}: {
  value: string;
  onChange: (v: string) => void;
  withdrawEnabled: boolean;
}) {
  const { t } = useTranslation("wallet");
  const modes: { value: string; label: string; desc: string; icon: LucideIcon }[] = [
    {
      value: "deposit",
      label: t("crypto.modeDeposit"),
      desc: t("crypto.modeDepositDesc"),
      icon: ArrowDownToLine,
    },
    ...(withdrawEnabled
      ? [
          {
            value: "withdraw",
            label: t("crypto.modeWithdraw"),
            desc: t("crypto.modeWithdrawDesc"),
            icon: ArrowUpFromLine,
          },
        ]
      : []),
  ];
  // With withdrawals off there is only one mode — a one-button switcher is
  // noise, and the deposit panel explains itself.
  if (modes.length < 2) return null;

  return (
    <div role="tablist" aria-label={t("crypto.modeSwitchLabel")} className="grid grid-cols-2 gap-2 sm:gap-3">
      {modes.map((m) => {
        const active = m.value === value || (m.value === "deposit" && value !== "withdraw");
        return (
          <button
            key={m.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(m.value)}
            className={cn(
              "flex items-center gap-3 rounded-2xl p-3 text-left transition-colors sm:p-4",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/55",
              active ? "bg-primary/10" : "bg-surface-2 hover:bg-surface-3",
            )}
          >
            <span
              className={cn(
                "grid h-10 w-10 shrink-0 place-items-center rounded-xl",
                active ? "bg-primary text-primary-fg" : "bg-surface-3 text-fg-muted",
              )}
            >
              <m.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className={cn("block text-sm font-semibold sm:text-base", active ? "text-primary" : "text-fg")}>
                {m.label}
              </span>
              <span className="hidden text-xs text-fg-muted sm:block">{m.desc}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
